import React, { useState } from 'react';
import { AxiosResponse } from 'axios';

// i18n
import { useTranslation } from '../../translations/useTranslation';

// Interfaces
import { CgiCollectResult, CgiProgressStatus } from '../../interfaces/API';
import { CgiPollingStatus } from '../../interfaces/CGI';

// Components
import { Layout } from '../../components/Layout';
import { Heading } from '../../components/Heading';
import { Button } from '../../components/Button';
import { CgiProviderLogo } from '../../components/CGI/CgiProviderLogo';
import { CgiAnotherDevice } from '../../components/CGI/CgiAnotherDevice';
import { CgiSameDevice } from '../../components/CGI/CgiSameDevice';

// Hooks
import useCgiPolling from '../../hooks/useCgiPolling';

// Context
import { useForm } from '../../context/form-context/hooks';
import {
  setContactInformation,
  setFormScene,
  setFormViewState,
  setUnion,
  setUserAge,
} from '../../context/form-context/actions';

// Utils
import { bisnodeResultToContactInformation, getProviderNameFromProvider } from '../../utilities/utils';
import { rebaseQuestionForMember } from '../../utilities/questionnaire';

interface CgiState {
  pollingInterval: number;
  pollingStatus: CgiPollingStatus;
  qrCodeDate?: string;
  pollingError?: string;
}

const PING_DELAY = 2000; // How many seconds after a response we try again

export const PollingView: React.FC = () => {
  const { state: formState, dispatch } = useForm();

  // Local page state used when polling
  const [state, setState] = useState<CgiState>({
    pollingInterval: PING_DELAY,
    pollingStatus: CgiPollingStatus.WAITING_FOR_SIGNATURE,
  });

  const { t, m, keys } = useTranslation();

  useCgiPolling({
    cacheKey: 'cgi-polling',
    interval: state.pollingInterval,
    orderRef: formState.orderRef,
    qrStartToken: formState.qrStartToken,
    provider: formState.provider,
    requestStartedMilliseconds: formState.requestStartedMilliseconds,
    onSuccess: (response: AxiosResponse<CgiCollectResult>) => {
      // User authenticated
      if (response.data.ProgressStatus === CgiProgressStatus.COMPLETE) {
        setState({
          ...state,
          pollingInterval: 0,
          pollingStatus: CgiPollingStatus.SIGNATURE_COMPLETE,
        });
        if (dispatch) {
          const contactInfo = bisnodeResultToContactInformation(
            response.data.BisnodeResult
          );
          contactInfo.personalIdentityNumber =
            response.data.PersonalIdentityNumber || '';
          dispatch(setContactInformation(contactInfo));

          if (response.data.IsYoungEnough) {
            if (
              response.data.AipResult &&
              Object.keys(response.data.AipResult).length > 0
            ) {
              dispatch(setUnion(response.data?.AipResult?.Union));
              dispatch(
                setFormViewState({
                  questionnaire: rebaseQuestionForMember(
                    formState.formViewState.questionnaire
                  ),
                })
              );
              dispatch(setFormScene('ALREADY-A-MEMBER'));
            } else {
              dispatch(setFormScene('FORM'));
            }
          } else {
            dispatch(setFormScene('TOO-OLD'));
            dispatch(setUserAge(response.data.UserAge));
          }
        }
      } else {
        // User has not authenticated yet, keep polling
        setState({
          ...state,
          pollingStatus:
            response.data.ProgressStatus === CgiProgressStatus.USER_SIGN
              ? CgiPollingStatus.APP_OPEN
              : state.pollingStatus,
          qrCodeDate: response.data.QrCodeData,
        });
      }
    },
    onError: () => {
      // Handle different errors?
      setState({
        ...state,
        pollingInterval: 0,
        qrCodeDate: undefined,
        pollingStatus: CgiPollingStatus.ERROR,
        pollingError: `${t(keys.uiUnspecifiedError)} ${t(
          keys.uiTryOnAnotherDeviceMessage
        )}`,
      });
    },
  });

  const handleOnCancel = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    event.preventDefault();
    setState({
      ...state,
      pollingInterval: 0,
      pollingStatus: CgiPollingStatus.CANCELED,
    });
    if (dispatch) {
      dispatch(setFormScene('START'));
    }
  };

  return (
    <Layout viewId={'polling'}>
      <CgiProviderLogo size="medium" provider={formState.provider} />
      <Heading size={'large'}>
        {m(
          keys.cgiSignInTitle,
          '{provider}',
          getProviderNameFromProvider(formState.provider) || ''
        )}
      </Heading>

      {formState.authenticationType === 'other-device' ? (
        <CgiAnotherDevice
          qrCodeDate={state.qrCodeDate}
          pollingStatus={state.pollingStatus}
          pollingError={state.pollingError}
        />
      ) : (
        formState.authenticationType === 'same-device' && (
          <CgiSameDevice
            autoStartLink={formState.autoStartLink}
            pollingStatus={state.pollingStatus}
            pollingError={state.pollingError}
          />
        )
      )}

      {state.pollingStatus !== CgiPollingStatus.ERROR && (
        <a href="#0" onClick={handleOnCancel} role="button" tabIndex={0}>
          {t(keys.uiCancel)}
        </a>
      )}
      {state.pollingStatus === CgiPollingStatus.ERROR && (
        <Button title={t(keys.uiTryAgain)} onClick={handleOnCancel} />
      )}
    </Layout>
  );
};
